import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, Minus, Sparkles, Info, X } from "lucide-react";
import { fetchJson, assetUrl } from "../lib/api";
import FighterImage from "../components/FighterImage";

/** Small movement indicator rendered next to a ranked fighter. */
function Movement({ type, delta }) {
  if (type === "up") {
    return (
      <span
        data-testid="movement-up"
        className="inline-flex items-center gap-1 text-burkina-green font-stats text-xs md:text-sm font-bold"
        title={`Monté de ${delta} place${delta > 1 ? "s" : ""}`}
      >
        <ArrowUp size={14} /> {delta}
      </span>
    );
  }
  if (type === "down") {
    return (
      <span
        data-testid="movement-down"
        className="inline-flex items-center gap-1 text-burkina-red font-stats text-xs md:text-sm font-bold"
        title={`Descendu de ${Math.abs(delta)} place${Math.abs(delta) > 1 ? "s" : ""}`}
      >
        <ArrowDown size={14} /> {Math.abs(delta)}
      </span>
    );
  }
  if (type === "new") {
    return (
      <span
        data-testid="movement-new"
        className="inline-flex items-center gap-1 text-gold font-stats text-[10px] md:text-xs font-bold uppercase tracking-widest"
        title="Nouvelle entrée dans le classement"
      >
        <Sparkles size={12} /> NEW
      </span>
    );
  }
  return (
    <span
      data-testid="movement-stable"
      className="inline-flex items-center gap-1 text-neutral-600 font-stats text-xs md:text-sm"
      title="Position inchangée"
    >
      <Minus size={14} />
    </span>
  );
}

function HowItWorks({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="rankings-howto-modal"
    >
      <div
        className="max-w-lg w-full bg-[#0a0a0a] border border-gold/40 p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <p className="text-[10px] font-stats uppercase tracking-[0.3em] text-gold">
            Info
          </p>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <X size={16} />
          </button>
        </div>
        <h3 className="mt-3 font-display font-black text-2xl md:text-3xl text-white uppercase leading-tight">
          Comment fonctionne le classement ?
        </h3>
        <p className="mt-4 text-neutral-300 text-sm md:text-base leading-relaxed">
          Les rankings BUFL sont calculés à partir des performances officielles
          enregistrées dans la ligue. Le système prend notamment en compte les
          résultats, la qualité des adversaires, les méthodes de victoire, la
          récence et l&apos;activité des combattants.
        </p>
        <p className="mt-3 text-neutral-500 text-xs md:text-sm leading-relaxed">
          Le comité BUFL peut, dans des cas exceptionnels, ajuster manuellement
          la position d&apos;un combattant. Ces exceptions sont clairement
          identifiées.
        </p>
      </div>
    </div>
  );
}

export default function Rankings() {
  const [tab, setTab] = useState("division"); // "division" | "p4p"
  const [gender, setGender] = useState("M"); // "M" | "F" — Phase 11
  const [level, setLevel] = useState("pro"); // "pro" | "amateur" — Phase A
  const [weightClasses, setWeightClasses] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [buflId, setBuflId] = useState(null);
  const [wcId, setWcId] = useState(null);
  const [rows, setRows] = useState([]);
  const [p4pRows, setP4pRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [howToOpen, setHowToOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const [wcs, lgs] = await Promise.all([
        fetchJson("/weight-classes"),
        fetchJson("/leagues"),
      ]);
      setLeagues(lgs || []);
      const bufl = lgs?.find((l) => l.abbreviation === "BUFL");
      setBuflId(bufl?.id || null);
      const flyBufl = (wcs || []).find(
        (w) => w.league_id === bufl?.id && w.display_order === 1,
      );
      setWeightClasses((wcs || []).filter((w) => w.league_id === bufl?.id));
      setWcId(flyBufl?.id || wcs?.[0]?.id || null);
    })();
  }, []);

  useEffect(() => {
    if (tab !== "division" || !wcId) return;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchJson("/rankings", { weight_class_id: wcId, gender, level });
        setRows(data || []);
      } catch (_) {
        setRows([]);
      }
      setLoading(false);
    })();
  }, [wcId, tab, gender, level]);

  useEffect(() => {
    if (tab !== "p4p" || !buflId) return;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchJson("/rankings/p4p", { league_id: buflId, gender, level, limit: 10 });
        setP4pRows(data || []);
      } catch (_) {
        setP4pRows([]);
      }
      setLoading(false);
    })();
  }, [tab, buflId, gender, level]);

  const currentWC = useMemo(
    () => weightClasses.find((w) => w.id === wcId),
    [weightClasses, wcId],
  );

  const champion = rows.find((r) => r.is_champion);
  const ranked = rows.filter((r) => !r.is_champion && r.position !== null);
  const hasOfficial = rows.some((r) => r.has_official);

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8" data-testid="rankings-page">
      <div className="pt-14 md:pt-20 pb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
          Classement officiel BUFL
        </p>
        <h1 className="mt-4 font-display font-black text-6xl md:text-9xl leading-[0.85] text-white">
          RANKINGS
        </h1>
        <p className="mt-4 text-neutral-500 text-sm max-w-2xl">
          Classement automatique de la ligue, mis à jour après chaque combat officiel.
        </p>
        <button
          onClick={() => setHowToOpen(true)}
          data-testid="rankings-howto-open"
          className="mt-4 inline-flex items-center gap-2 text-[11px] font-stats uppercase tracking-widest text-neutral-500 hover:text-gold transition-colors border-b border-transparent hover:border-gold/40 pb-0.5"
        >
          <Info size={11} /> Comment fonctionne le classement ?
        </button>
      </div>

      {/* Top-level tabs: DIVISIONS vs P4P */}
      <div className="border-y border-neutral-900 py-4">
        <div className="flex flex-wrap items-center gap-3 md:gap-4">
          <div className="flex gap-2 md:gap-3">
            <button
              onClick={() => setTab("division")}
              data-testid="rankings-tab-division"
              className={`px-4 py-2 text-[11px] font-display uppercase tracking-widest border transition-colors duration-200 ${
                tab === "division"
                  ? "border-gold text-gold bg-black"
                  : "border-neutral-900 text-neutral-500 hover:text-white hover:border-neutral-700"
              }`}
            >
              Divisions
            </button>
            <button
              onClick={() => setTab("p4p")}
              data-testid="rankings-tab-p4p"
              className={`px-4 py-2 text-[11px] font-display uppercase tracking-widest border transition-colors duration-200 ${
                tab === "p4p"
                  ? "border-gold text-gold bg-black"
                  : "border-neutral-900 text-neutral-500 hover:text-white hover:border-neutral-700"
              }`}
            >
              Pound-for-Pound
            </button>
          </div>

          {/* Level toggle — Phase A · amateur / pro strict separation */}
          <div
            className="flex items-center border border-neutral-900 ml-auto md:ml-0"
            role="tablist"
            aria-label="Niveau"
          >
            <button
              onClick={() => setLevel("pro")}
              data-testid="rankings-level-pro"
              role="tab"
              aria-selected={level === "pro"}
              className={`px-3 md:px-4 py-2 text-[11px] font-display uppercase tracking-widest transition-colors duration-200 ${
                level === "pro"
                  ? "bg-gold text-black"
                  : "text-neutral-500 hover:text-white"
              }`}
            >
              Pro
            </button>
            <button
              onClick={() => setLevel("amateur")}
              data-testid="rankings-level-amateur"
              role="tab"
              aria-selected={level === "amateur"}
              className={`px-3 md:px-4 py-2 text-[11px] font-display uppercase tracking-widest transition-colors duration-200 ${
                level === "amateur"
                  ? "bg-gold text-black"
                  : "text-neutral-500 hover:text-white"
              }`}
            >
              Amateur
            </button>
          </div>

          {/* Gender toggle — Phase 11 */}
          <div
            className="flex items-center border border-neutral-900 ml-auto"
            role="tablist"
            aria-label="Genre"
          >
            <button
              onClick={() => setGender("M")}
              data-testid="rankings-gender-M"
              role="tab"
              aria-selected={gender === "M"}
              className={`px-3 md:px-4 py-2 text-[11px] font-display uppercase tracking-widest transition-colors duration-200 ${
                gender === "M"
                  ? "bg-white text-black"
                  : "text-neutral-500 hover:text-white"
              }`}
            >
              Hommes
            </button>
            <button
              onClick={() => setGender("F")}
              data-testid="rankings-gender-F"
              role="tab"
              aria-selected={gender === "F"}
              className={`px-3 md:px-4 py-2 text-[11px] font-display uppercase tracking-widest transition-colors duration-200 ${
                gender === "F"
                  ? "bg-white text-black"
                  : "text-neutral-500 hover:text-white"
              }`}
            >
              Femmes
            </button>
          </div>
        </div>
      </div>

      {tab === "division" && (
        <>
          {/* Division tabs */}
          <div className="border-b border-neutral-900 py-4 overflow-x-auto">
            <div className="flex gap-2 md:gap-3 min-w-min">
              {weightClasses.map((w) => (
                <button
                  key={w.id}
                  onClick={() => setWcId(w.id)}
                  data-testid={`ranking-tab-${w.display_order}`}
                  className={`px-4 py-2 text-[11px] font-display uppercase tracking-widest border transition-colors duration-200 whitespace-nowrap ${
                    wcId === w.id
                      ? "border-white text-white bg-black"
                      : "border-neutral-900 text-neutral-500 hover:text-white hover:border-neutral-700"
                  }`}
                >
                  {w.name.split(" (")[0]}
                </button>
              ))}
            </div>
          </div>

          <div className="py-10">
            {loading ? (
              <p className="text-neutral-600 font-stats text-xs uppercase tracking-widest animate-pulse">
                Chargement…
              </p>
            ) : (
              <>
                {/* R1.3 — CHAMPION — VACANT band on every division regardless
                    of whether anyone is ranked yet. No fighter can currently
                    hold a title (champion status is never auto-derived). */}
                <ChampionBand champion={champion} weightClass={currentWC} />
                {rows.length === 0 ? (
                  <div
                    className="border border-neutral-900 border-t-0 line-grid py-16 px-8 text-center"
                    data-testid="rankings-empty"
                  >
                    <p className="font-display font-black text-3xl md:text-5xl text-neutral-600">
                      CLASSEMENT VIDE
                    </p>
                    <p className="mt-3 text-neutral-500 text-sm">
                      Aucun combattant classé dans cette catégorie pour l&apos;instant.
                    </p>
                  </div>
                ) : (
                  <div className="border-t border-neutral-900">
                    {ranked.map((row) => (
                      <RankRow key={row.fighter.id} row={row} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {tab === "p4p" && (
        <div className="py-10">
          <div className="mb-6">
            <p className="text-[10px] font-stats uppercase tracking-[0.3em] text-gold">
              Toutes catégories · BUFL · {level === "amateur" ? "Amateur" : "Pro"} · {gender === "F" ? "Femmes" : "Hommes"}
            </p>
            <h2 className="mt-2 font-display font-black text-4xl md:text-6xl text-white uppercase leading-none">
              Pound-for-Pound
            </h2>
            <p className="mt-3 text-neutral-500 text-sm max-w-2xl">
              Les 10 {gender === "F" ? "meilleures combattantes" : "meilleurs combattants"} BUFL toutes catégories confondues.
            </p>
          </div>
          {loading ? (
            <p className="text-neutral-600 font-stats text-xs uppercase tracking-widest animate-pulse">
              Chargement…
            </p>
          ) : p4pRows.length === 0 ? (
            <div className="border border-neutral-900 line-grid py-20 px-8 text-center" data-testid="p4p-empty">
              <p className="font-display font-black text-3xl md:text-5xl text-neutral-600">
                P4P À VENIR
              </p>
              <p className="mt-3 text-neutral-500 text-sm">
                Le classement pound-for-pound sera publié dès que suffisamment de
                combats officiels seront enregistrés.
              </p>
            </div>
          ) : (
            <div className="border-t border-neutral-900" data-testid="p4p-list">
              {/* Phase R2 — Provisional tag surfaced once at the top of
                  the P4P list when the backend flags the entire bucket
                  as provisional (ligue < N BUFL fights). Never covers
                  the individual position — the classification simply
                  isn't yet statistically settled. */}
              {p4pRows.some((r) => r.is_provisional) && (
                <div
                  className="border-b border-dashed border-neutral-800 py-3 px-4 flex items-center gap-3"
                  data-testid="p4p-provisional-band"
                >
                  <span className="font-display font-black text-xs tracking-widest text-gold">
                    PROVISOIRE
                  </span>
                  <span className="text-[11px] text-neutral-500">
                    Trop peu de combats BUFL enregistrés dans la ligue —
                    ce classement se stabilisera au fil des cartes officielles.
                  </span>
                </div>
              )}
              {p4pRows.map((row) => (
                <RankRow key={row.fighter.id} row={row} isP4P />
              ))}
            </div>
          )}
        </div>
      )}

      <HowItWorks open={howToOpen} onClose={() => setHowToOpen(false)} />
    </div>
  );
}

function RankRow({ row, isP4P = false }) {
  const f = row.fighter;
  const pos = row.position;
  return (
    <Link
      to={`/fighters/${f.slug}`}
      data-testid={`ranking-row-${pos}`}
      className="grid grid-cols-12 gap-2 md:gap-3 items-center py-4 md:py-6 border-b border-neutral-900 group hover:bg-neutral-950/60 transition-colors duration-200"
    >
      <div className="col-span-2 md:col-span-1 font-stats font-bold text-neutral-300 text-2xl md:text-4xl">
        #{pos}
      </div>
      <div className="col-span-2 md:col-span-1">
        <FighterImage
          src={assetUrl(f.photo_url)}
          alt={f.first_name}
          className="w-12 h-12 md:w-16 md:h-16"
          imgClassName="object-cover object-top"
        />
      </div>
      <div className="col-span-8 md:col-span-5 pl-2 md:pl-4 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="font-display font-black text-white text-xl md:text-3xl leading-none uppercase truncate">
            {f.first_name}
          </p>
          {row.is_champion && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-stats uppercase tracking-widest border border-gold/60 text-gold shrink-0">
              C
            </span>
          )}
        </div>
        {f.nickname && (
          <p className="mt-1 text-gold italic text-xs truncate">&quot;{f.nickname}&quot;</p>
        )}
        {isP4P && row.weight_class?.name && (
          <p className="mt-1 text-[10px] font-stats uppercase tracking-widest text-neutral-500 truncate">
            {row.weight_class.name.split(" (")[0]}
          </p>
        )}
      </div>
      <div className="col-span-6 md:col-span-3 font-stats text-xs md:text-sm text-neutral-400 uppercase tracking-widest mt-2 md:mt-0">
        {f.display_record?.record_str || f.record?.record_str}
      </div>
      <div className="col-span-6 md:col-span-2 text-right md:text-left mt-2 md:mt-0 flex items-center justify-end md:justify-start gap-2">
        <Movement type={row.movement_type} delta={row.movement} />
        {row.has_manual_override && (
          <span
            className="inline-block px-1.5 py-0.5 text-[9px] font-stats uppercase tracking-widest border border-neutral-800 text-neutral-500"
            title="Position ajustée par le comité BUFL"
            data-testid={`override-badge-${pos}`}
          >
            Ajusté
          </span>
        )}
      </div>
    </Link>
  );
}

/** Champion strip — either shows the current champion or "VACANT". */
function ChampionBand({ champion, weightClass }) {
  const f = champion?.fighter;
  return (
    <div
      data-testid="champion-band"
      className="relative overflow-hidden border border-gold/40 mb-6 md:mb-10"
    >
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold rounded-full blur-3xl" />
      </div>

      {f ? (
        <Link
          to={`/fighters/${f.slug}`}
          className="relative grid grid-cols-12 gap-3 items-center px-3 py-5 md:px-6 md:py-7 group"
        >
          <div className="col-span-2 md:col-span-1 flex items-start">
            <span className="font-display font-black text-gold text-5xl md:text-8xl leading-none">
              C
            </span>
          </div>
          <div className="col-span-2 md:col-span-1">
            <FighterImage
              src={assetUrl(f.photo_url)}
              alt={f.first_name}
              className="w-14 h-14 md:w-20 md:h-20 border border-gold/40"
              imgClassName="object-cover object-top"
            />
          </div>
          <div className="col-span-8 md:col-span-6 pl-2 md:pl-4 min-w-0">
            <p className="text-[10px] font-stats uppercase tracking-[0.3em] text-gold">
              Champion en titre
              {f.title_defenses > 0 && (
                <span className="ml-2 text-neutral-500">
                  · {f.title_defenses} défense{f.title_defenses > 1 ? "s" : ""}
                </span>
              )}
            </p>
            <p className="mt-1 font-display font-black text-white text-2xl md:text-5xl leading-none uppercase truncate">
              {f.first_name}
            </p>
            {f.nickname && (
              <p className="mt-1 text-gold italic text-sm md:text-base truncate">&quot;{f.nickname}&quot;</p>
            )}
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right mt-2 md:mt-0">
            <p className="font-stats text-xs md:text-base text-neutral-300 uppercase tracking-widest">
              {f.display_record?.record_str || f.record?.record_str}
            </p>
            <p className="mt-1 text-[10px] font-stats uppercase tracking-widest text-neutral-500 truncate">
              {weightClass?.name?.split(" (")[0]}
            </p>
          </div>
        </Link>
      ) : (
        <div
          data-testid="champion-vacant"
          className="relative px-4 py-6 md:px-6 md:py-8 flex items-center gap-4 md:gap-6"
        >
          <span className="font-display font-black text-gold/40 text-5xl md:text-8xl leading-none">
            C
          </span>
          <div>
            <p className="text-[10px] font-stats uppercase tracking-[0.3em] text-gold">
              Titre {weightClass?.name?.split(" (")[0] || ""}
            </p>
            <p className="mt-1 font-display font-black text-white text-3xl md:text-5xl leading-none">
              VACANT
            </p>
            <p className="mt-2 text-neutral-500 text-xs md:text-sm max-w-md">
              Aucun champion en titre. Le prochain sacre sera décidé sur le ring.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
